import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { MoreVertical } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { AvatarGroupHorizontal, Stack, Typography } from '@lyearn/ui';
import { TypographyColors, TypographyVariants } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import { getRandomAvatar } from '@/modules/session/utils/getRandomAvatar';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { KnowledgeAssetContentType, ResourceStatusEnum } from '@/types/schema';

import { getClassifiedChildKnowledgeAssets } from '../../utils';
import { renderKnowledgeAssetStatusPill } from '../StatusPill';

export interface UserDetailsOverrides {
  Email?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Name?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Icon?: {
    className?: string;
    size?: number;
  };
}

function PersonaJourneyCardGrid({
  personaJourney,
  index,
  hideActionMenu = true,
  isLearner,
  editOnClick = false,
  showStatusPill = false,
}: {
  personaJourney: ContentsFragment;
  index: number;
  hideActionMenu?: boolean;
  isLearner?: boolean;
  editOnClick?: boolean;
  showStatusPill?: boolean;
}) {
  const { t } = useTranslation();
  const { id, name } = personaJourney;
  const description = (personaJourney as KnowledgeAssetContentType).description;
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();
  const onClick = useCallback(() => {
    if (editOnClick) {
      navigation.push(routes.KnowledgeAssetEditLearner(id, 'persona-journey'), {
        appendQuery: false,
      });
      return;
    }
    if (isLearner || !isAdminRoute) {
      navigation.push(routes.KnowledgeAssetLearnerDetails(id, 'persona-journey'), {
        appendQuery: false,
      });
      return;
    }
    const isDraft =
      (personaJourney as KnowledgeAssetContentType).status === ResourceStatusEnum.Draft;
    if (isDraft) {
      navigation.replace(routes.KnowledgeAssetEditor(id, 'persona-journey'), {
        appendQuery: false,
      });
    } else {
      navigation.replace(routes.KnowledgeAssetDetails(id, 'persona-journey'), {
        appendQuery: false,
      });
    }
  }, [id, navigation, isLearner, editOnClick, isAdminRoute, routes]);

  const { solutions, personas } = getClassifiedChildKnowledgeAssets(
    personaJourney as KnowledgeAssetContentType,
  );

  const personaStr = personas.length ? personas?.[0] : 'No personas';
  const plusN = personas && personas?.length > 1 ? ` +${personas?.length - 1}` : null;

  const avatars = personas
    .map((persona) => ({
      name: persona,
      src:
        (personaJourney as KnowledgeAssetContentType)?.childKnowledgeAssets?.find(
          (child) => child?.name === persona,
        )?.cover?.url || getRandomAvatar(),
    }))
    .slice(0, 4);

  const solutionStr =
    solutions && solutions.length
      ? solutions?.[0] + (solutions?.length > 1 ? ` +${solutions?.length - 1}` : '')
      : null;

  const statusPill = useMemo(
    () =>
      showStatusPill
        ? renderKnowledgeAssetStatusPill(personaJourney as KnowledgeAssetContentType, t)
        : null,
    [personaJourney, showStatusPill, t],
  );

  return (
    <Stack
      className="w-[32.4rem] rounded-12 border cursor-pointer border-field px-20 py-20 hover:shadow-01 transition-shadow xs:min-h-full xs:w-full"
      direction="col"
      gapY={24}
      onClick={onClick}>
      <Stack gapX={12} justifyContent="between">
        <Stack direction="col">
          <Stack className="w-full overflow-hidden">
            <Typography numberOfLines={1} variant="heading-xs">
              {name}
            </Typography>
          </Stack>
          <Stack>
            <Typography color="text-text-secondary" numberOfLines={1} variant="label-02">
              {solutionStr}
            </Typography>
          </Stack>
        </Stack>

        {hideActionMenu ? null : (
          <Stack className="flex-shrink-0">
            <MoreVertical className="text-icon-secondary" height={20} width={20} />
          </Stack>
        )}
      </Stack>

      {/* Tagged personas */}
      <Stack className="mt-auto">
        {statusPill ? (
          statusPill
        ) : (
          <Stack
            alignItems="center"
            className={cn(
              'bg-bg-tertiary rounded-24 pe-14 w-fit',
              personas.length ? '' : 'py-[0.7rem] ps-14 pe-14',
            )}
            direction="row"
            gapX={6}
            justifyContent="start">
            <AvatarGroupHorizontal
              avatars={avatars}
              classes={{ border: 'border-bg-tertiary' }}
              size="small"
            />
            <Stack className="max-w-[12rem]">
              <TypographyWithTooltip
                as="span"
                color={personas.length ? 'text-text-primary' : 'text-text-secondary'}
                numberOfLines={1}
                variant="body-short-02">
                {personaStr}
              </TypographyWithTooltip>
            </Stack>
            {plusN ? (
              <Typography
                className="flex-shrink-0 me-6"
                color="text-text-primary"
                numberOfLines={1}
                variant="body-short-02">
                {plusN}
              </Typography>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}

export default PersonaJourneyCardGrid;
